/**
 * Audit Log Helper
 * 
 * Helper functions สำหรับบันทึก Audit Log จากที่ต่างๆ
 * 
 * Note: Backend triggers จะบันทึก log โดยอัตโนมัติ แต่ helper นี้ใช้สำหรับ
 * กรณีที่ต้องการบันทึก log เพิ่มเติมจาก client
 */

import { 
  EntityType, 
  AuditLogChange,
  CreateAuditLogInput,
} from './audit-log.types'

// ============================================
// Helper Functions
// ============================================

/**
 * สร้าง Audit Log Input สำหรับการ Login
 */
export function createLoginLog(
  username: string,
  success: boolean,
  errorMessage?: string
): CreateAuditLogInput {
  return {
    action: success ? 'login' : 'login_failed',
    entityType: 'Admin',
    entityId: username,
    entityName: username,
    description: success 
      ? `เข้าสู่ระบบ Admin Panel: ${username}` 
      : `เข้าสู่ระบบไม่สำเร็จ: ${username}`,
    severity: success ? 'info' : 'warning',
    status: success ? 'success' : 'failed',
    errorMessage,
  }
}

/**
 * สร้าง Audit Log Input สำหรับการ Logout
 */
export function createLogoutLog(
  userId: string,
  username: string
): CreateAuditLogInput {
  return {
    action: 'logout',
    entityType: 'Admin',
    entityId: userId,
    entityName: username,
    description: `ออกจากระบบ: ${username}`,
    severity: 'info',
    status: 'success',
  }
}

/**
 * สร้าง Audit Log Input สำหรับการสร้าง Member
 */
export function createMemberCreatedLog(
  memberId: string,
  memberName: string,
  phone: string
): CreateAuditLogInput {
  return {
    action: 'create',
    entityType: 'Member',
    entityId: memberId,
    entityName: memberName,
    description: `สร้างสมาชิกใหม่: ${memberName} (${phone})`,
    severity: 'info',
    status: 'success',
  }
}

/**
 * สร้าง Audit Log Input สำหรับการแก้ไข Member
 */
export function createMemberUpdatedLog(
  memberId: string,
  memberName: string,
  changes: AuditLogChange[]
): CreateAuditLogInput {
  const changesSummary = changes.length > 0 
    ? ` (${changes.map(c => c.fieldLabel || c.field).join(', ')})`
    : ''
  
  return {
    action: 'update',
    entityType: 'Member',
    entityId: memberId,
    entityName: memberName,
    description: `แก้ไขข้อมูลสมาชิก: ${memberName}${changesSummary}`,
    changes,
    severity: 'info',
    status: 'success',
  }
}

/**
 * สร้าง Audit Log Input สำหรับการอัปเดต Settings
 */
export function createSettingsUpdatedLog(
  category: string,
  changes: AuditLogChange[]
): CreateAuditLogInput {
  const categoryLabel = {
    general: 'ทั่วไป',
    finance: 'การเงิน',
    agent: 'ตัวแทน',
    commission: 'ค่าคอมมิชชั่น',
    slip: 'Slip Verify',
  }[category] || category
  
  return {
    action: 'update_setting',
    entityType: 'AppConfig',
    entityId: 'main',
    entityName: 'การตั้งค่าระบบ',
    description: `อัปเดตการตั้งค่าระบบ (หมวด: ${categoryLabel})`,
    changes,
    severity: 'warning',
    status: 'success',
    metadata: { category },
  }
}

/**
 * สร้าง Audit Log Input สำหรับการอนุมัติ/ปฏิเสธ
 */
export function createApprovalLog(
  approved: boolean,
  entityType: EntityType,
  entityId: string,
  entityName: string,
  reason?: string
): CreateAuditLogInput {
  return {
    action: approved ? 'approve' : 'reject',
    entityType,
    entityId,
    entityName,
    description: approved 
      ? `อนุมัติ ${entityName}` 
      : `ปฏิเสธ ${entityName}${reason ? `: ${reason}` : ''}`,
    severity: approved ? 'info' : 'warning',
    status: 'success',
    metadata: reason ? { reason } : undefined,
  }
}

/**
 * สร้าง Audit Log Input สำหรับการฝาก/ถอน
 */
export function createTransactionLog(
  action: 'deposit' | 'withdraw',
  transactionId: string,
  memberName: string,
  amount: number,
  success: boolean,
  errorMessage?: string
): CreateAuditLogInput {
  const actionLabel = action === 'deposit' ? 'ฝากเงิน' : 'ถอนเงิน'
  
  return {
    action,
    entityType: 'DepositWithdraw',
    entityId: transactionId,
    entityName: `${actionLabel} - ${memberName}`,
    description: `${actionLabel}: ${memberName} จำนวน ${amount.toLocaleString()} บาท`,
    severity: success ? 'info' : 'error',
    status: success ? 'success' : 'failed',
    errorMessage,
    metadata: { amount },
  }
}

/**
 * Compare two objects and return changes
 */
export function compareObjects<T extends Record<string, any>>(
  original: T,
  updated: T,
  fieldLabels?: Record<string, string>
): AuditLogChange[] {
  const changes: AuditLogChange[] = []
  
  const allKeys = new Set([...Object.keys(original), ...Object.keys(updated)])
  
  for (const key of allKeys) {
    const oldValue = original[key]
    const newValue = updated[key]
    
    // Skip functions, undefined, and unchanged values
    if (typeof oldValue === 'function' || typeof newValue === 'function') continue
    if (oldValue === undefined && newValue === undefined) continue
    if (JSON.stringify(oldValue) === JSON.stringify(newValue)) continue
    
    changes.push({
      field: key,
      fieldLabel: fieldLabels?.[key] || key,
      oldValue: oldValue ?? null,
      newValue: newValue ?? null,
    })
  }
  
  return changes
}

// ============================================
// Field Labels (Thai)
// ============================================

export const MEMBER_FIELD_LABELS: Record<string, string> = {
  firstname: 'ชื่อ',
  lastname: 'นามสกุล',
  mobile_phone: 'เบอร์โทรศัพท์',
  phone: 'เบอร์โทรศัพท์',
  bank_name: 'ธนาคาร',
  bank_account: 'เลขบัญชี',
  status: 'สถานะ',
  balance: 'ยอดเงินคงเหลือ',
  password: 'รหัสผ่าน',
  agent_username: 'Agent Username',
  agent_password: 'Agent Password',
  refer_user: 'ผู้แนะนำ',
}

export const SETTINGS_FIELD_LABELS: Record<string, string> = {
  site_name: 'ชื่อเว็บไซต์',
  site_logo: 'โลโก้',
  maintenance_mode: 'โหมดปิดปรับปรุง',
  deposit_min: 'ฝากขั้นต่ำ',
  deposit_max: 'ฝากสูงสุด',
  withdraw_min: 'ถอนขั้นต่ำ',
  withdraw_max: 'ถอนสูงสุด',
  deposit_min_with_slip: 'ฝากขั้นต่ำ (ใช้สลิป)',
  withdraw_limit: 'ลิมิตถอนต่อครั้ง',
  withdraw_max_perday: 'ถอนสูงสุดต่อวัน (ครั้ง)',
  withdraw_max_perday_amount: 'ถอนสูงสุดต่อวัน (จำนวนเงิน)',
  AGENT_USERNAME: 'Agent Username',
  AGENT_KEY: 'Agent Key',
  AGENT_WEB: 'Agent Web',
}








